import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { ClientEnvironmentConfig } from '@core/config-models';
import { getBAGRegistration } from '@essent/contract';
import {
  getInstallationAddresses as getInstallationAddressesAction,
  getInstallationAddressesError,
  getInstallationAddressesSuccess,
} from '@essent/installation-address';
import { AlternativeHousenumberExtensions } from '@innogy/become-a-customer/shared/interfaces';
import { KEY_PRESS_DEBOUNCE_TIME } from '@innogy/become-a-customer/shared/testing';
import {
  BAC_QUESTIONNAIRE_ID,
  expectedConsumptionDefinedAction,
  SupplyType,
} from '@innogy/common-ui/sales';
import { ofControlId } from '@innogy/utils-deprecated';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store, type Action } from '@ngrx/store';
import {
  DisableAction,
  EnableAction,
  FocusAction,
  MarkAsDirtyAction,
  SetValueAction,
} from 'ngrx-forms';
import { debounceTime, filter, map, mergeMap } from 'rxjs/operators';

import { resetBAGRegistrationFaultyAction } from '../bac/bag-registration';
import {
  calculateFormLoadedAction,
  clearCalculateFormUpdatedAfterAddressCheckAction,
  disableCalculateCrntHouseNumberAdditionDropdownAction,
  disableCalculateManualHouseNumberAdditionInputAction,
  resetHousenumberExtensionAction,
} from './calculate.actions';
import {
  doubleMeterFormControlId,
  electricityUsageFormControlId,
  electricityUsageNormalFormControlId,
  electricityUsageOffPeakFormControlId,
  gasUsageFormControlId,
  houseNumberAdditionAltSelectionFormControlId,
  houseNumberAdditionFormControlId,
  houseNumberFormControlId,
  postalCodeFormControlId,
} from './calculate.reducer';
import {
  getCalculateFormFormState,
  getCalculateFormIsDoubleMeter,
  getCalculateRenderingValue,
} from './calculate.selectors';
import { getInstallationAddressState } from '../bac/installation-address';

@Injectable()
export class CalculateFormEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store$: Store<any>,
    @Inject(ENVIRONMENT_CONFIG)
    private readonly environmentConfig: ClientEnvironmentConfig
  ) {}

  private readonly formValues$ = this.store$.pipe(
    select(getCalculateFormFormState)
  );

  private readonly isDoubleMeter$ = this.store$.pipe(
    select(getCalculateFormIsDoubleMeter)
  );

  private readonly useSupervisedManualHouseNumberAddition$ = this.store$.pipe(
    select(
      getCalculateRenderingValue(
        'UseSupervisedManualHouseNumberAddition',
        false
      )
    )
  );

  private readonly useAccessibleCalculator$ = this.store$.pipe(
    select(getCalculateRenderingValue('UseAccessibleCalculator', false))
  );

  private readonly installationAddressState$ = this.store$.pipe(
    select(getInstallationAddressState)
  );

  /**
   * When the calculate-form loads the values might be already filled in. This may happen if the user navigates from the offer-overview
   * back to the calcualte page. In this case the address check needs to be performed make the form valid and to fetch all existing
   * house number extensions.
   */
  public getInstallationAddressWhenCalculateFormStateFilled$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(calculateFormLoadedAction),
        concatLatestFrom(() => [this.formValues$]),
        filter(([_, formState]) => {
          const formControls = formState.controls;

          return (
            formControls.postalCode.isValid &&
            !!formControls.houseNumber &&
            formControls.houseNumber.isValid
          );
        }),
        map(([_, formState]) => {
          const formValues = formState.value;

          return getInstallationAddressesAction({
            payload: {
              postcode: formValues.postalCode || '',
              houseNumber: formValues.houseNumber?.toString() || '',
            },
          });
        })
      )
  );

  /**
   * Split or join usage values based on doubleMeter bool value
   */
  public fillElectricityControlsSingleMeter$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SetValueAction<number>>(SetValueAction.TYPE),
      ofControlId(doubleMeterFormControlId),
      concatLatestFrom(() => [this.isDoubleMeter$]),
      filter(([, isDoubleMeter]) => !isDoubleMeter),
      concatLatestFrom(() => [this.formValues$]),
      mergeMap(([_, state]) => {
        const usageNormal = state.value.electricityUsageNormal as number;
        const usageOffPeak = state.value.electricityUsageOffPeak as number;
        const total = usageNormal + usageOffPeak;
        return [
          new SetValueAction(
            electricityUsageFormControlId,
            Math.floor(total === 0 ? NaN : total)
          ),
        ];
      })
    )
  );

  public fillElectricityControlsDoubleMeter$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SetValueAction<number>>(SetValueAction.TYPE),
      ofControlId(doubleMeterFormControlId),
      concatLatestFrom(() => [this.isDoubleMeter$]),
      filter(([, isDoubleMeter]) => isDoubleMeter),
      concatLatestFrom(() => [this.formValues$]),
      mergeMap(([_, state]) => {
        const usage = state.value.electricityUsage as number;
        const usageNormal = state.value.electricityUsageNormal as number;
        const usageOffPeak = state.value.electricityUsageOffPeak as number;
        // Is usage is invalid, do not set normal and offpeak usage
        if (
          (state.controls.electricityUsage?.isInvalid || usage === 0) &&
          Number.isNaN(usageNormal) &&
          Number.isNaN(usageOffPeak)
        ) {
          return [];
        }

        const normal = Math.ceil(usage / 2);
        const offPeak = Math.floor(usage - normal);
        return [
          new SetValueAction(
            electricityUsageNormalFormControlId,
            Number.isNaN(usageNormal) ? normal : usageNormal
          ),
          new SetValueAction(
            electricityUsageOffPeakFormControlId,
            Number.isNaN(usageOffPeak) ? offPeak : usageOffPeak
          ),
        ];
      })
    )
  );

  /**
   * Fetch installation address when address fields are valid en lost focus
   */
  public readonly getBAGRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SetValueAction<any>>(SetValueAction.TYPE),
      filter(() => this.environmentConfig.segment === 'zakelijk'),
      filter(({ controlId }) =>
        [
          postalCodeFormControlId,
          houseNumberFormControlId,
          houseNumberAdditionFormControlId,
        ].includes(controlId)
      ),
      debounceTime(KEY_PRESS_DEBOUNCE_TIME),
      concatLatestFrom(() => [this.formValues$]),
      filter(([, { controls, value }]) => {
        const isPostalValid =
          !!controls.postalCode?.isValid && !!value.postalCode;
        const isHouseNumberValid =
          !!controls.houseNumber?.isValid && !!value.houseNumber;
        const isAdditionValid = controls.houseNumberAddition.isValid;
        return isPostalValid && isHouseNumberValid && isAdditionValid;
      }),

      mergeMap(([_, state]) => {
        return [
          resetBAGRegistrationFaultyAction(),
          getBAGRegistration({
            payload: {
              postcode: state.value.postalCode,
              houseNumber: state.value.houseNumber?.toString() ?? '',
              houseNumberExtension: state.value.houseNumberAddition,
            },
          }),
        ];
      })
    )
  );

  /**
   * Disable Crnt House Number addition fields when fields are invalid
   */
  public readonly disableCrntCalculatorHouseNumberAddition$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SetValueAction<any>>(SetValueAction.TYPE),
      filter(
        (action) =>
          action.controlId === postalCodeFormControlId ||
          action.controlId === houseNumberFormControlId
      ),
      concatLatestFrom(() => [this.formValues$, this.useAccessibleCalculator$]),
      filter(([, { controls, value }, useAccessibleCalculator]) => {
        const isPostalValid =
          !!controls.postalCode?.isValid && !!value.postalCode;
        const isHouseNumberValid =
          !!controls.houseNumber?.isValid && !!value.houseNumber;
        return (
          (!isPostalValid || !isHouseNumberValid) && useAccessibleCalculator
        );
      }),
      mergeMap(() => {
        return [
          resetHousenumberExtensionAction(),
          new DisableAction(houseNumberAdditionFormControlId),
          new DisableAction(houseNumberAdditionAltSelectionFormControlId),
        ];
      })
    )
  );

  /**
   * Fetch installation address when address fields are valid en lost focus
   */
  public readonly fetchInstallationAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SetValueAction<any>>(SetValueAction.TYPE),
      filter(
        (action) =>
          action.controlId === postalCodeFormControlId ||
          action.controlId === houseNumberFormControlId
      ),
      debounceTime(KEY_PRESS_DEBOUNCE_TIME),
      concatLatestFrom(() => [
        this.formValues$,
        this.useSupervisedManualHouseNumberAddition$,
        this.useAccessibleCalculator$,
      ]),
      filter(([, { controls, value }]) => {
        const isPostalValid =
          !!controls.postalCode?.isValid && !!value.postalCode;
        const isHouseNumberValid =
          !!controls.houseNumber?.isValid && !!value.houseNumber;
        return isPostalValid && isHouseNumberValid;
      }),
      mergeMap(
        ([
          _,
          state,
          supervisedManualHouseNumberAddition,
          useAccessibleCalculator,
        ]) => {
          const actions: Action[] = [
            getInstallationAddressesAction({
              payload: {
                postcode: state.value.postalCode,
                houseNumber: state.value.houseNumber?.toString() ?? '',
              },
            }),
            clearCalculateFormUpdatedAfterAddressCheckAction(),
          ];

          if (
            !supervisedManualHouseNumberAddition &&
            !useAccessibleCalculator
          ) {
            actions.push(resetHousenumberExtensionAction());
          }

          if (useAccessibleCalculator) {
            actions.push(
              new EnableAction(houseNumberAdditionAltSelectionFormControlId)
            );
            actions.push(new EnableAction(houseNumberAdditionFormControlId));
          }

          return actions;
        }
      )
    )
  );

  /**
   * Disable the CRNT House Number Addition field on load
   */
  public disableCalculateCrntHouseNumberAdditionDropdown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(disableCalculateCrntHouseNumberAdditionDropdownAction),
      map(() => new DisableAction(houseNumberAdditionFormControlId))
    )
  );

  /**
   * Disable extension field when checking address with API
   */
  public disableHouseNumberExtensionField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getInstallationAddressesAction),
      concatLatestFrom(() => [this.useSupervisedManualHouseNumberAddition$]),
      filter(
        ([_, supervisedManualHouseNumberAddition]) =>
          !supervisedManualHouseNumberAddition
      ),
      map(() => new DisableAction(houseNumberAdditionFormControlId))
    )
  );

  /**
   * Enable extension field when API call has a response
   */
  public enableHouseNumberExtensionField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getInstallationAddressesSuccess, getInstallationAddressesError),
      concatLatestFrom(() => this.useSupervisedManualHouseNumberAddition$),
      filter(
        ([_, supervisedManualHouseNumberAddition]) =>
          !supervisedManualHouseNumberAddition
      ),
      map(() => new EnableAction(houseNumberAdditionFormControlId))
    )
  );

  public enableHouseNumberExtensionAltSelectionField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getInstallationAddressesSuccess),
      concatLatestFrom(() => [
        this.formValues$,
        this.useSupervisedManualHouseNumberAddition$,
      ]),
      filter(
        ([_, __, supervisedManualHouseNumberAddition]) =>
          supervisedManualHouseNumberAddition
      ),
      mergeMap(
        ([
          { payload },
          {
            value: { houseNumberAddition },
          },
        ]) => {
          const matchedAddress = payload.find(
            (address) =>
              (!address.houseNumberExtension && !houseNumberAddition) ||
              address.houseNumberExtension === houseNumberAddition
          );
          return !matchedAddress
            ? [new EnableAction(houseNumberAdditionAltSelectionFormControlId)]
            : [];
        }
      )
    )
  );

  public disableHouseNumberExtensionAltSelectionFieldOnSelection$ =
    createEffect(() =>
      this.actions$.pipe(
        ofType<SetValueAction<any>>(SetValueAction.TYPE),
        concatLatestFrom(() => [
          this.formValues$,
          this.useSupervisedManualHouseNumberAddition$,
          this.useAccessibleCalculator$,
        ]),
        filter(
          ([action, _, useSupervisedManualHouseNumberAddition]) =>
            action.controlId === houseNumberAdditionAltSelectionFormControlId &&
            action.value !== AlternativeHousenumberExtensions.OTHER &&
            useSupervisedManualHouseNumberAddition
        ),
        mergeMap(([{ value }, , , useAccessibleCalculator]) => {
          if (useAccessibleCalculator) {
            return [
              resetHousenumberExtensionAction(),
              new SetValueAction(houseNumberAdditionFormControlId, value),
            ];
          }

          return [
            new SetValueAction(houseNumberAdditionFormControlId, value),
            new DisableAction(houseNumberAdditionAltSelectionFormControlId),
          ];
        })
      )
    );

  public toggleHouseNumberExtensionAltSelectionFieldOnManualInput$ =
    createEffect(() =>
      this.actions$.pipe(
        ofType<SetValueAction<any>>(SetValueAction.TYPE),
        concatLatestFrom(() => [
          this.useSupervisedManualHouseNumberAddition$,
          this.useAccessibleCalculator$,
        ]),
        filter(
          ([action, useSupervisedManualHouseNumberAddition]) =>
            action.controlId === houseNumberAdditionFormControlId &&
            useSupervisedManualHouseNumberAddition
        ),
        debounceTime(KEY_PRESS_DEBOUNCE_TIME),
        concatLatestFrom(() => [
          this.formValues$,
          this.installationAddressState$,
          this.useAccessibleCalculator$,
        ]),
        mergeMap(
          ([[action, _, useAccessibleCalculator], formState, addressState]) => {
            const addressInputMatch = addressState.data?.find(
              (address) =>
                (!address.houseNumberExtension && !action.value) ||
                address.houseNumberExtension?.toLowerCase() ===
                  action.value.toString().toLowerCase()
            );

            if (
              formState.controls.houseNumberAdditionAltSelection.value === '' &&
              useAccessibleCalculator
            ) {
              return [resetHousenumberExtensionAction()];
            }

            if (
              !addressInputMatch &&
              formState.controls.houseNumberAdditionAltSelection.isDisabled
            ) {
              return [
                new EnableAction(houseNumberAdditionAltSelectionFormControlId),
              ];
            } else if (
              addressInputMatch &&
              formState.controls.houseNumberAdditionAltSelection.isEnabled
            ) {
              return [
                new DisableAction(houseNumberAdditionAltSelectionFormControlId),
              ];
            }

            if (
              formState.controls.houseNumberAdditionAltSelection.value !==
              AlternativeHousenumberExtensions.OTHER
            ) {
              return [disableCalculateManualHouseNumberAdditionInputAction()];
            }

            return [];
          }
        )
      )
    );

  public applySingleMeterEstimateToCalculateForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(expectedConsumptionDefinedAction),
      filter(
        (expectedConsumption) =>
          expectedConsumption.formId === BAC_QUESTIONNAIRE_ID
      ),
      concatLatestFrom(() => [this.isDoubleMeter$]),
      filter(([, doubleMeter]) => !doubleMeter),
      mergeMap(([expectedConsumption]) => {
        const { supplyType, expectedConsumption: estimate } =
          expectedConsumption;
        return [
          new SetValueAction(
            electricityUsageFormControlId,
            hasElectricityConnection(supplyType)
              ? estimate.electricity.total
              : NaN
          ),
          new SetValueAction(
            gasUsageFormControlId,
            hasGasConnection(supplyType) ? estimate.gas : NaN
          ),
          new FocusAction(electricityUsageFormControlId),
          new FocusAction(gasUsageFormControlId),
          new MarkAsDirtyAction(electricityUsageFormControlId),
          new MarkAsDirtyAction(gasUsageFormControlId),
        ];
      })
    )
  );

  public applyDoubleMeterEstimateToCalculateForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(expectedConsumptionDefinedAction),
      filter(
        (expectedConsumption) =>
          expectedConsumption.formId === BAC_QUESTIONNAIRE_ID
      ),
      concatLatestFrom(() => [this.isDoubleMeter$]),
      filter(([, doubleMeter]) => doubleMeter),
      mergeMap(([expectedConsumption]) => {
        const { supplyType, expectedConsumption: estimate } =
          expectedConsumption;
        return [
          new SetValueAction(
            electricityUsageNormalFormControlId,
            hasElectricityConnection(supplyType)
              ? Math.ceil(estimate.electricity.high)
              : NaN
          ),
          new SetValueAction(
            electricityUsageOffPeakFormControlId,
            hasElectricityConnection(supplyType)
              ? Math.floor(estimate.electricity.low)
              : NaN
          ),
          new SetValueAction(
            gasUsageFormControlId,
            hasGasConnection(supplyType) ? estimate.gas : NaN
          ),
          new FocusAction(electricityUsageNormalFormControlId),
          new FocusAction(electricityUsageOffPeakFormControlId),
          new FocusAction(gasUsageFormControlId),
          new MarkAsDirtyAction(electricityUsageNormalFormControlId),
          new MarkAsDirtyAction(electricityUsageOffPeakFormControlId),
          new MarkAsDirtyAction(gasUsageFormControlId),
        ];
      })
    )
  );
}

const hasGasConnection = (supplyType?: SupplyType) =>
  supplyType === SupplyType.E_AND_G || supplyType === SupplyType.G_ONLY;
const hasElectricityConnection = (supplyType?: SupplyType) =>
  supplyType === SupplyType.E_AND_G || supplyType === SupplyType.E_ONLY;
